import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const NotificationContext = createContext(null);

export const useNotification = () => useContext(NotificationContext);

let idCounter = 0;

/**
 * Notification types: 'info' | 'success' | 'error' | 'job'
 * 'job' type toasts stay open and update in place as steps come in.
 */
export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  // jobId → notificationId mapping so we can update in place
  const jobToastMap = useRef({});

  const remove = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  /** Show a simple one-shot toast */
  const notify = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++idCounter;
    setNotifications(prev => [...prev, { id, message, type }]);
    if (duration > 0) setTimeout(() => remove(id), duration);
    return id;
  }, [remove]);

  /**
   * Called when a backup/deploy job starts.
   * Creates a persistent 'job' toast that will be updated by notifyJobStep.
   */
  const notifyJobStart = useCallback((jobId, label) => {
    const id = ++idCounter;
    jobToastMap.current[jobId] = id;
    setNotifications(prev => [
      ...prev,
      { id, type: 'job', jobId, label, step: 'Starting…', status: 'running' }
    ]);
    return id;
  }, []);

  /**
   * Update the live job toast with the latest step message.
   * status: 'running' | 'completed' | 'failed'
   */
  const notifyJobStep = useCallback((jobId, step, status = 'running') => {
    const id = jobToastMap.current[jobId];
    console.log('inside notifyjobstep>>>', id, jobId, jobToastMap.current)
    if (!id) return;

    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, step, status } : n)
    );

    // Auto-dismiss after a short delay on terminal states
    if (status === 'completed' || status === 'failed') {
      setTimeout(() => {
        remove(id);
        delete jobToastMap.current[jobId];
      }, 5000);
    }
  }, [remove]);

  return (
    <NotificationContext.Provider value={{ notify, notifyJobStart, notifyJobStep }}>
      {children}
      <NotificationContainer notifications={notifications} onRemove={remove} />
    </NotificationContext.Provider>
  );
}

// ─── Toast UI ────────────────────────────────────────────────────────────────

const STEP_LABELS = {
  connect:  '🔌 Connecting to server',
  mkdir:    '📁 Preparing workspace',
  mongodb:  '🗄️  Dumping database',
  copy:     '📋 Copying app files',
  compress: '🗜️  Compressing backup',
  s3:       '☁️  Uploading to S3',
  cleanup:  '🧹 Cleaning up',
  complete: '✅ All done',
  error:    '❌ Error occurred',
};

function resolveStepLabel(step) {
  if (!step) return 'Working…';
  // step can be the raw step key OR a full message string from current_step
  return STEP_LABELS[step] || step;
}

function NotificationContainer({ notifications, onRemove }) {
  if (!notifications.length) return null;

  return (
    <div style={styles.container}>
      {notifications.map(n => (
        n.type === 'job'
          ? <JobToast key={n.id} n={n} onRemove={onRemove} />
          : <SimpleToast key={n.id} n={n} onRemove={onRemove} />
      ))}
    </div>
  );
}

function SimpleToast({ n, onRemove }) {
  const colors = {
    info:    { bg: '#1e293b', accent: '#38bdf8', icon: 'ℹ️' },
    success: { bg: '#14532d', accent: '#4ade80', icon: '✅' },
    error:   { bg: '#450a0a', accent: '#f87171', icon: '❌' },
  };
  const c = colors[n.type] || colors.info;

  return (
    <div style={{ ...styles.toast, background: c.bg, borderLeft: `3px solid ${c.accent}` }}>
      <span style={{ marginRight: 8 }}>{c.icon}</span>
      <span style={styles.message}>{n.message}</span>
      <button style={styles.close} onClick={() => onRemove(n.id)}>×</button>
    </div>
  );
}

function JobToast({ n, onRemove }) {
  const isComplete = n.status === 'completed';
  const isFailed   = n.status === 'failed';
  const isDone     = isComplete || isFailed;

  const accent = isFailed ? '#f87171' : isComplete ? '#4ade80' : '#38bdf8';
  const bg     = isFailed ? '#450a0a' : '#0f172a';

  return (
    <div style={{ ...styles.toast, background: bg, borderLeft: `3px solid ${accent}`, minWidth: 280 }}>
      <div style={{ flex: 1 }}>
        <div style={{ ...styles.jobLabel, color: accent }}>
          {isFailed ? '❌' : isComplete ? '✅' : <Spinner />}
          &nbsp;{n.label}
        </div>
        <div style={styles.jobStep}>{resolveStepLabel(n.step)}</div>
        {!isDone && <div style={{ ...styles.progressBar, '--accent': accent }} />}
      </div>
      {isDone && (
        <button style={styles.close} onClick={() => onRemove(n.id)}>×</button>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <span style={styles.spinner} />
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  container: {
    position: 'fixed',
    top: 16,
    left: '50%', // Center horizontally
    transform: 'translateX(-50%)', // Pull back by half its width
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    maxWidth: 360,
    width: '100%',
    pointerEvents: 'none',
    alignItems: 'center', // Ensures toasts stay centered if they are narrower than maxWidth
  },
  toast: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '12px 14px',
    borderRadius: 8,
    boxShadow: '0 4px 24px rgba(0,0,0,0.45)',
    color: '#f1f5f9',
    fontSize: 13,
    fontFamily: 'monospace',
    pointerEvents: 'all',
    animation: 'slideIn 0.3s ease-out', // Referenced below
    width: 'fit-content', // Collapse to message size
    minWidth: 280,
  },
  message: {
    flex: 1,
    lineHeight: 1.4,
  },
  close: {
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    fontSize: 18,
    lineHeight: 1,
    padding: 0,
    marginLeft: 4,
    flexShrink: 0,
  },
  jobLabel: {
    fontWeight: 700,
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  jobStep: {
    color: '#94a3b8',
    fontSize: 12,
    marginBottom: 6,
  },
  progressBar: {
    height: 2,
    background: 'rgba(255,255,255,0.1)',
    borderRadius: 2,
    overflow: 'hidden',
    position: 'relative',
  },
  spinner: {
    display: 'inline-block',
    width: 10,
    height: 10,
    border: '2px solid rgba(255,255,255,0.2)',
    borderTopColor: '#38bdf8',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
};

// Inject keyframes once
if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.textContent = `
    @keyframes slideIn {
      from { opacity: 0; transform: translateX(30px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    @keyframes indeterminate {
      0%   { left: -40%; width: 40%; }
      100% { left: 100%; width: 40%; }
    }
  `;
  document.head.appendChild(styleEl);
}
